import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    CardActionArea,
    CircularProgress,
    Alert,
    Chip,
    Avatar,
    IconButton,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Folder as FolderIcon,
    ArrowBack as ArrowBackIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import TEXT from './text';
import { AppGlobalContext } from '../../../App';
import * as componentActions from './Actions';
import DbGroupDashboard from '../../Body/DbGroupDashboard/DbGroupDashboard';

const Dashboard = ({ ...props }) => {
    const language = useSelector(state => state.App_Settings.language);
    const userMetadata = useSelector(state => state.UserMetaData);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [view, setView] = useState('list'); // 'list' or 'detail'

    // Load accessible database groups
    useEffect(() => {
        loadDatabaseGroups();
    }, []);

    const loadDatabaseGroups = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(
                window.devMode ? 'http://localhost:80/get_accessible_items' : '/get_accessible_items',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: userMetadata.email })
                }
            );

            const data = await response.json();
            console.log('[Dashboard] Received data:', data);
            
            // Handle different response formats
            let groupsData = [];
            
            if (data.groups) {
                groupsData = data.groups;
            } else if (data.dbgroups) {
                groupsData = data.dbgroups;
            } else if (data.accessible_groups) {
                groupsData = data.accessible_groups;
            } else if (Array.isArray(data)) {
                groupsData = data;
            } else if (data.status === 'success' && data.data) {
                groupsData = data.data.groups || data.data.dbgroups || [];
            }
            
            console.log('[Dashboard] Parsed groups:', groupsData);
            
            // Ensure each group has required fields
            const formattedGroups = groupsData.map(group => ({
                group_id: group.group_id || group.id || group.groupId,
                group_name: group.group_name || group.name || group.groupName || `Group ${group.group_id || group.id}`,
                version: group.version || 1,
                database_count: group.database_count || group.databases?.length || 0
            }));
            
            setGroups(formattedGroups);
            
            if (formattedGroups.length === 0) {
                console.warn('[Dashboard] No groups found in response');
            }
        } catch (err) {
            console.error('[Dashboard] Error loading groups:', err);
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
        setView('detail');
    };

    const handleBackToList = () => {
        setSelectedGroup(null);
        setView('list');
    };

    // Render detail view (individual group dashboard)
    if (view === 'detail' && selectedGroup) {
        return (
            <DbGroupDashboard
                groupId={selectedGroup.group_id}
                groupName={selectedGroup.group_name}
                onBack={handleBackToList}
            />
        );
    }

    // Render list view (all groups)
    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
                <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                    <Typography variant="h4" component="h1">
                        {TEXT.Dashboard?.[language] || 'Dashboard'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {TEXT.SelectGroupToView?.[language] || 'Select a database group to view its dashboard'}
                    </Typography>
                </Box>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && !loading && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Groups Grid */}
            {!loading && !error && groups.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                        {TEXT.NoGroupsAvailable?.[language] || 'No database groups available'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {TEXT.CreateGroupToStart?.[language] || 'Create a database group to get started'}
                    </Typography>
                </Paper>
            )}

            {!loading && !error && groups.length > 0 && (
                <Grid container spacing={3}>
                    {groups.map((group) => (
                        <Grid item xs={12} sm={6} md={4} key={group.group_id}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: 6
                                    }
                                }}
                            >
                                <CardActionArea onClick={() => handleGroupClick(group)}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                                                <FolderIcon />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography 
                                                    variant="h6" 
                                                    component="div"
                                                    sx={{ 
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {group.group_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    ID: {group.group_id}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                                            {group.database_count > 0 && (
                                                <Chip 
                                                    size="small" 
                                                    label={`${group.database_count} ${TEXT.Databases?.[language] || 'Databases'}`}
                                                    variant="outlined"
                                                />
                                            )}
                                            {group.version && (
                                                <Chip 
                                                    size="small" 
                                                    label={`v${group.version}`}
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUpIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                {TEXT.ViewDashboard?.[language] || 'View Dashboard'}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
};

export default Dashboard;
